import type { ScheduleExpectationSummary } from '@/types/schedule';
import type { TimeUnit } from '@/types/scheduleExpectation';

export const HORIZON_WEEKS = 12;

const WEEKS_PER_UNIT: Record<TimeUnit, number> = {
  day: 1 / 7,
  week: 1,
  year: 52
};

const toWeeks = (value: number, unit: TimeUnit) => value * WEEKS_PER_UNIT[unit];

const percentOfHorizon = (weeks: number, horizonWeeks: number) =>
  (Math.min(weeks, horizonWeeks) / horizonWeeks) * 100;

export interface TimelineBlock {
  leftPct: number;
  widthPct: number;
  dotCount: number;
}

export interface TimelineRow {
  appointmentTypeId: string;
  appointmentTypeName: string;
  blocks: TimelineBlock[];
  advanceWidthPct: number;
  hasTail: boolean;
  tailLeftPct: number;
  tailLabel: string;
}

/**
 * Ports the design's "first N weeks of care" preview math, generalized so it
 * works for expectations in any `time_unit` (the design assumed everything
 * was already in weeks) by converting cycle/look-ahead lengths to a common
 * weeks-based horizon before laying out bar positions.
 */
export const buildTimelineRow = (
  expectation: ScheduleExpectationSummary,
  horizonWeeks = HORIZON_WEEKS
): TimelineRow => {
  const cycleWeeks = toWeeks(expectation.cycle_time, expectation.time_unit);
  const isOneTime = expectation.max_attendances === 1;
  const cycleCount = isOneTime ? 1 : Math.ceil(horizonWeeks / cycleWeeks);

  const blocks: TimelineBlock[] = [];
  for (let i = 0; i < cycleCount; i++) {
    const start = i * cycleWeeks;
    if (start >= horizonWeeks) break;
    blocks.push({
      leftPct: percentOfHorizon(start, horizonWeeks),
      widthPct: percentOfHorizon(Math.min(cycleWeeks, horizonWeeks - start), horizonWeeks),
      dotCount: expectation.frequency
    });
  }

  return {
    appointmentTypeId: expectation.appointment_type_id,
    appointmentTypeName: expectation.appointment_type_name,
    blocks,
    advanceWidthPct: percentOfHorizon(expectation.look_ahead_cycles * cycleWeeks, horizonWeeks),
    hasTail: isOneTime && cycleWeeks < horizonWeeks,
    tailLeftPct: percentOfHorizon(cycleWeeks, horizonWeeks),
    tailLabel: 'Expectation met — no further appointments'
  };
};
